import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardFooter, Button } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useCart } from "../contexts/cart-context";
import api from "../services/api";
import { ErrorDisplay, LoadingDisplay } from "../components/error-boundary";

interface Product {
  id: number;
  name: string;
  category: string;
  scent: string;
  price: number;
  image: string;
  slug?: string;
  is_exclusive?: boolean;
}

interface FeaturedProductsProps {
  onProductSelect: (productType: string) => void;
}

export const FeaturedProducts: React.FC<FeaturedProductsProps> = ({ onProductSelect }) => {
  // Add cart context
  const { addItem } = useCart();
  
  const [products, setProducts] = React.useState<Product[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await api.get('/products/collections/');
        const data = resp?.data;
        if (cancelled) return;
        // Expect an array of up to 6 product objects
        if (Array.isArray(data)) {
          setProducts(data.slice(0, 6));
        } else if (Array.isArray(data?.results)) {
          setProducts(data.results.slice(0, 6));
        } else {
          setProducts([]);
        }
      } catch (e: any) {
        console.error('Failed to load collections', e);
        if (!cancelled) setError(e?.response?.data?.message || 'Не удалось загрузить коллекцию');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Normalize incoming product objects from API to the shape this component expects
  const normalizeProduct = (p: any): Product => {
    const id = Number(p.id ?? p.pk ?? p.product_id ?? 0);
    const name = String(p.name ?? p.title ?? p.product_name ?? '');
    let category = '';
    if (!p.category) category = '';
    else if (typeof p.category === 'string') category = p.category;
    else if (typeof p.category === 'object') category = String(p.category.name ?? p.category.key ?? '');

    const scent = String(p.scent ?? p.subtitle ?? p.description ?? '');
    const price = Number(p.price ?? p.price_amount ?? p.current_price ?? 0);

    let image = '';
    // Prefer main_image if backend provides it
    if (p.main_image) {
      if (typeof p.main_image === 'string') image = p.main_image;
      else if (typeof p.main_image === 'object') image = p.main_image.url ?? p.main_image.path ?? '';
    }
    // fallback to image / images
    if (!image) {
      if (!p.image) {
        // try images array
        if (Array.isArray(p.images) && p.images[0]) image = p.images[0].url ?? p.images[0];
      } else if (typeof p.image === 'string') {
        image = p.image;
      } else if (typeof p.image === 'object') {
        image = p.image.url ?? p.image.path ?? '';
      }
    }

    // Fallback placeholder (keeps previous behavior minimal)
    if (!image) image = `https://img.heroui.chat/image/furniture?w=600&h=800&u=product-${id}`;

    // Try to extract slug if backend provides it under common keys
    const slug = (p.slug || p.slugified || p.permalink || p.slug_name || p.url_slug || p.handle || null) as string | null;

    const is_exclusive = Boolean((p as any).is_exclusive);

    return { id, name, category, scent, price, image, ...(slug ? { slug } : {}), ...(is_exclusive ? { is_exclusive } : {}) };
  };

  const normalizedProducts = React.useMemo(() => products.map(normalizeProduct), [products]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Define product categories for the filter buttons
  const productCategories = [
    { key: "candles", label: "Свечи" },
    { key: "diffusers", label: "Диффузоры" },
    { key: "sachets", label: "Саше" },
  ];

  // Updated function to handle product click with router
  const handleProductClick = (productId: number) => {
    // No need to call onProductSelect as we'll use Link component directly
  };

  return (
    <section className="py-24 bg-background" id="catalog">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-light mb-4">Наша <span className="font-medium">коллекция</span></h2>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            Каждый продукт создан вручную из премиальных материалов с использованием уникальных ароматических композиций
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            {productCategories.map((category) => (
              <Button
                as={Link}
                to={`/catalog/${category.key}`}
                key={category.key}
                color="default"
                variant="flat"
                className="font-medium"
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <LoadingDisplay message="Загрузка популярных товаров..." />
        ) : error ? (
          <ErrorDisplay message="Не удалось загрузить товары. Пожалуйста, попробуйте позже." />
        ) : (
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {normalizedProducts.map((product) => (
              <motion.div key={product.id} variants={itemVariants}>
                <Card 
                  className="border border-divider bg-content2/50 product-card-hover product-card h-full flex flex-col"
                  disableRipple
                >
                  <CardBody className="p-0 overflow-hidden flex flex-col">
                    <Link to={`/product-detail/${product.slug || product.id}`}> 
                      <div className="relative h-80 overflow-hidden cursor-pointer group">
                        <img 
                          src={product.image} 
                          alt={product.name} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm px-3 py-1">
                          <p className="text-xs font-medium">{product.category}</p>
                        </div>
                        {product.is_exclusive && (
                          <div className="absolute top-4 right-4 z-20 inline-flex items-center gap-1 rounded-full bg-purple-700/90 text-white px-3 py-1 text-[11px] font-medium tracking-wide uppercase shadow-sm">
                            <Icon icon="lucide:sparkles" width={14} />
                            <span>Эксклюзив</span>
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* body content: title, price, scent placed in CardBody so titles align across cards */}
                    <div className="p-6">
                      <div className="flex justify-between w-full items-start">
                        <div className="pr-4">
                            <Link to={`/product-detail/${product.slug || product.id}`}> 
                            <h3 className="text-base font-medium hover:underline cursor-pointer whitespace-normal break-words">{product.name}</h3>
                          </Link>
                        </div>
                        <p className="font-medium whitespace-nowrap">{product.price} ₽</p>
                      </div>
                      <p className="text-sm text-foreground/70 mt-3 line-clamp-2">{product.scent}</p>
                    </div>
                  </CardBody>

                  {/* spacer to push footer to bottom so cards in a row align by height */}
                  <div className="flex-grow" />

                  <CardFooter className="p-6">
                    <Button 
                      color="primary" 
                      variant="flat" 
                      className="w-full"
                      endContent={<Icon icon="lucide:shopping-bag" width={18} />}
                      onPress={() => {
                        // Add item to cart
                        addItem({
                          id: product.id,
                          name: product.name,
                          slug: product.slug,
                          category: product.category,
                          price: product.price,
                          image: product.image
                        });
                      }}
                    >
                      В корзину
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <Button
            as={Link}
            to="/catalog"
            color="default"
            variant="bordered"
            size="lg"
            className="font-medium"
            endContent={<Icon icon="lucide:arrow-right" width={18} />}
          >
            Смотреть весь каталог
          </Button>
        </div>
      </div>
    </section>
  );
};